import { useState, useRef, ReactNode, ElementRef } from 'react';
import { useI18n } from 'next-localization';
import classnames from 'classnames';

type TooltipProps = {
  arialLabelText?: string;
  tooltipContentPosition?: string;
  tooltipTrigger?: ReactNode;
  tooltipTitle?: ReactNode;
  tooltipContent?: ReactNode;
  className?: string;
};

const Tooltip = ({
  arialLabelText,
  tooltipContentPosition = 'top',
  tooltipTrigger,
  tooltipContent,
  tooltipTitle,
  className = '',
}: TooltipProps) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const isDefaultTooltip = !tooltipTrigger;
  const popupRef = useRef<ElementRef<'div'>>(null);
  const tooltipTriggerRef = useRef<ElementRef<'button'>>(null);

  const tooltipClassName = classnames({
    tooltip: true,
    'ms-1': isDefaultTooltip,
    className,
  });

  const handleClick = (state) => {
    setIsOpen(state);

    if (state === true) {
      popupRef.current?.focus();
    } else {
      tooltipTriggerRef.current?.focus();
    }
  };

  return (
    <div className={tooltipClassName} onBlur={() => setIsOpen(false)}>
      <button
        ref={tooltipTriggerRef}
        aria-label={t('moreInformation') + arialLabelText}
        aria-expanded={isOpen ? 'true' : 'false'}
        className="tooltip__trigger"
        aria-controls="popupContent"
        onClick={() => handleClick(!isOpen)}
      >
        {isDefaultTooltip ? <p className="tooltip-trigger">i</p> : tooltipTrigger}
      </button>

      {isOpen && (
        <div
          id="popupContent"
          ref={popupRef}
          className={`tooltip__content d-flex p-2 tooltip__content_${tooltipContentPosition}`}
          role="tooltip"
          tabIndex={-1}
          onKeyDown={(e) => e.key === 'Esc' && handleClick(false)}
        >
          <div>
            {tooltipTitle}
            {tooltipContent}
          </div>
          <button
            tabIndex={0}
            className="tooltip__closer"
            aria-label="Hide tooltip"
            onClick={() => handleClick(false)}
            onBlur={() => handleClick(false)}
          >
            <i className="bi bi-x" />
          </button>
        </div>
      )}
      <div
        className={classnames({ 'overlay d-lg-none': true, active: isOpen })}
        onClick={() => handleClick(false)}
      />
    </div>
  );
};

export default Tooltip;
